import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import { 
  Phone, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Users,
  Shield,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  UserX,
  UserCheck,
  Trash2,
  Archive
} from 'lucide-react';

const CallOutsManagementTab = () => {
  const [pendingCallouts, setPendingCallouts] = useState([]);
  const [allCallouts, setAllCallouts] = useState([]);
  const [disciplineReport, setDisciplineReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pending'); // pending, all, discipline
  const [reviewingId, setReviewingId] = useState(null);
  const [ownerNotes, setOwnerNotes] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [reactivating, setReactivating] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes, disciplineRes] = await Promise.all([
        api.get('/callouts/pending'),
        api.get('/callouts?limit=100'),
        api.get('/callouts/discipline-report')
      ]);
      setPendingCallouts(pendingRes.data.callouts || []);
      setAllCallouts(allRes.data.callouts || []);
      setDisciplineReport(disciplineRes.data);
    } catch (error) {
      console.error('Failed to fetch call out data:', error);
      toast.error('Failed to load call out data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleReview = async (calloutId, status) => {
    setReviewingId(calloutId);
    try {
      await api.put(`/callouts/${calloutId}/review`, {
        status: status,
        owner_notes: ownerNotes || null
      });
      
      toast.success(`Call out ${status === 'approved' ? 'approved' : 'rejected'}`);
      setOwnerNotes('');
      setExpandedId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review call out');
    } finally {
      setReviewingId(null);
    }
  };

  const handleReactivate = async (workerId) => {
    if (!window.confirm('Are you sure you want to reactivate this worker? Their call out count will remain but they will be able to work again.')) {
      return;
    }
    
    setReactivating(workerId);
    try {
      await api.post(`/callouts/${workerId}/reactivate`);
      toast.success('Worker reactivated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate worker');
    } finally {
      setReactivating(null);
    }
  };

  const handleDeleteCallout = async (calloutId) => {
    if (!window.confirm('Delete this call out record? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/callouts/${calloutId}`);
      toast.success('Call out deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete call out');
    }
  };

  const getDisciplineBadge = (level) => {
    const badges = {
      none: { color: 'bg-[var(--success)]/15 text-[var(--success)]', label: 'Good Standing' },
      warning: { color: 'bg-[var(--warning)]/15 text-[var(--warning)]', label: 'Warning' },
      second_warning: { color: 'bg-orange-100 text-orange-800', label: '2nd Warning' },
      probation: { color: 'bg-[var(--error)]/15 text-[var(--error)]', label: 'Probation' },
      deactivated: { color: 'bg-gray-800 text-white', label: 'Deactivated' }
    };
    const badge = badges[level] || badges.none;
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-[#D4AF37]/10 text-blue-800', icon: Clock },
      approved: { color: 'bg-[var(--success)]/15 text-[var(--success)]', icon: CheckCircle },
      rejected: { color: 'bg-[var(--error)]/15 text-[var(--error)]', icon: XCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="callouts-management-tab">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 mx-auto text-[#D4AF37] mb-1" />
            <p className="text-2xl font-bold text-[#D4AF37]">{pendingCallouts.length}</p>
            <p className="text-xs text-[#D4AF37]/70">Pending Review</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-100">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto text-yellow-500 mb-1" />
            <p className="text-2xl font-bold text-[var(--warning)]">{disciplineReport?.warning_list?.length || 0}</p>
            <p className="text-xs text-[var(--warning)]/70">Warning List</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-100">
          <CardContent className="p-4 text-center">
            <AlertCircle className="h-6 w-6 mx-auto text-orange-500 mb-1" />
            <p className="text-2xl font-bold text-orange-600">{disciplineReport?.second_warning_list?.length || 0}</p>
            <p className="text-xs text-orange-600/70">2nd Warning</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-red-100">
          <CardContent className="p-4 text-center">
            <Shield className="h-6 w-6 mx-auto text-[var(--error)] mb-1" />
            <p className="text-2xl font-bold text-[var(--error)]">{disciplineReport?.probation_list?.length || 0}</p>
            <p className="text-xs text-[var(--error)]/70">On Probation</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-100 to-gray-200 border-[var(--border-primary)]">
          <CardContent className="p-4 text-center">
            <UserX className="h-6 w-6 mx-auto text-[var(--text-secondary)] mb-1" />
            <p className="text-2xl font-bold text-[var(--text-primary)]">{disciplineReport?.deactivated_list?.length || 0}</p>
            <p className="text-xs text-[var(--text-secondary)]">Deactivated</p>
          </CardContent>
        </Card>
      </div>

      {/* View Tabs */}
      <div className="flex gap-2">
        <Button
          variant={view === 'pending' ? 'default' : 'outline'}
          onClick={() => setView('pending')}
          className={view === 'pending' ? 'bg-pink-500' : ''}
          data-testid="view-pending"
        >
          Pending ({pendingCallouts.length})
        </Button>
        <Button
          variant={view === 'discipline' ? 'default' : 'outline'}
          onClick={() => setView('discipline')}
          className={view === 'discipline' ? 'bg-pink-500' : ''}
          data-testid="view-discipline"
        >
          Discipline Report
        </Button>
        <Button
          variant={view === 'all' ? 'default' : 'outline'}
          onClick={() => setView('all')}
          className={view === 'all' ? 'bg-pink-500' : ''}
          data-testid="view-all"
        >
          All Call Outs
        </Button>
        <Button variant="outline" onClick={fetchData} className="ml-auto">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Pending Call Outs View */}
      {view === 'pending' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-pink-500" />
              Pending Call Out Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingCallouts.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-400" />
                <p>No pending call out requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingCallouts.map(callout => (
                  <div 
                    key={callout.id}
                    className="border rounded-lg overflow-hidden"
                    data-testid={`pending-callout-${callout.id}`}
                  >
                    {/* Header */}
                    <div 
                      className="flex items-center justify-between p-4 bg-[var(--background-subtle)] cursor-pointer"
                      onClick={() => setExpandedId(expandedId === callout.id ? null : callout.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{callout.worker_name}</span>
                            {callout.is_short_notice && (
                              <Badge className="bg-amber-100 text-amber-800">Short Notice</Badge>
                            )}
                            {getDisciplineBadge(callout.worker_stats?.discipline_level)}
                          </div>
                          <p className="text-sm text-[var(--text-secondary)]">
                            {callout.shift_date} - {callout.service_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{callout.callout_type}</Badge>
                        {expandedId === callout.id ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedId === callout.id && (
                      <div className="p-4 border-t space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-[var(--text-muted)]">Customer</p>
                            <p className="font-medium">{callout.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-[var(--text-muted)]">Days Notice</p>
                            <p className="font-medium">{callout.days_notice} days</p>
                          </div>
                          <div>
                            <p className="text-[var(--text-muted)]">Contacted Supervisor</p>
                            <p className="font-medium">
                              {callout.contacted_supervisor ? `Yes (${callout.supervisor_contact_method || 'N/A'})` : 'No'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[var(--text-muted)]">Call Outs This Year</p>
                            <p className="font-medium">{callout.worker_stats?.approved_count || 0}</p>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-[var(--text-muted)] text-sm">Reason</p>
                          <p className="bg-[var(--background-subtle)] p-3 rounded-lg mt-1">{callout.reason}</p>
                        </div>
                        
                        {/* Warning about discipline */}
                        {callout.worker_stats?.approved_count >= 2 && (
                          <Card className={`${
                            callout.worker_stats.approved_count >= 8 ? 'bg-[var(--error)]/10 border-[var(--error)]/30' :
                            callout.worker_stats.approved_count >= 4 ? 'bg-orange-50 border-orange-200' :
                            'bg-[var(--warning)]/10 border-[var(--warning)]/30'
                          }`}>
                            <CardContent className="p-3 flex items-center gap-2">
                              <AlertTriangle className="h-5 w-5" />
                              <span className="text-sm">
                                {callout.worker_stats.approved_count >= 8 
                                  ? 'Approving will put this worker on PROBATION or DEACTIVATE their account!'
                                  : callout.worker_stats.approved_count >= 4
                                    ? 'Approving will trigger a 2nd warning for this worker.'
                                    : 'Approving will trigger a warning for this worker.'
                                }
                              </span>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Owner Notes */}
                        <div>
                          <p className="text-sm font-medium mb-1">Notes (optional)</p>
                          <Textarea
                            value={ownerNotes}
                            onChange={(e) => setOwnerNotes(e.target.value)}
                            placeholder="Add notes for the worker (especially if rejecting)..."
                            rows={2}
                          />
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <Button
                            onClick={() => handleReview(callout.id, 'approved')}
                            disabled={reviewingId === callout.id}
                            className="bg-green-500 hover:bg-[var(--success)]"
                            data-testid={`approve-${callout.id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReview(callout.id, 'rejected')}
                            disabled={reviewingId === callout.id}
                            variant="destructive"
                            data-testid={`reject-${callout.id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            onClick={() => handleDeleteCallout(callout.id)}
                            variant="ghost"
                            className="text-[var(--error)] hover:text-red-700"
                            data-testid={`delete-${callout.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Discipline Report View */}
      {view === 'discipline' && (
        <div className="space-y-4">
          {/* Deactivated Workers */}
          {disciplineReport?.deactivated_list?.length > 0 && (
            <Card className="border-[var(--error)]/30">
              <CardHeader className="bg-[var(--error)]/10">
                <CardTitle className="text-[var(--error)] flex items-center gap-2">
                  <UserX className="h-5 w-5" />
                  Deactivated Workers (9+ Call Outs)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {disciplineReport.deactivated_list.map(worker => (
                    <div 
                      key={worker.worker_id}
                      className="flex items-center justify-between p-3 bg-[var(--error)]/10 rounded-lg"
                    >
                      <div>
                        <span className="font-medium">{worker.worker_name}</span>
                        <span className="text-sm text-[var(--error)] ml-2">
                          ({worker.approved_callouts} call outs)
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(worker.worker_id)}
                        disabled={reactivating === worker.worker_id}
                        className="bg-green-500 hover:bg-[var(--success)]"
                        data-testid={`reactivate-${worker.worker_id}`}
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        {reactivating === worker.worker_id ? 'Reactivating...' : 'Reactivate'}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Probation */}
          {disciplineReport?.probation_list?.length > 0 && (
            <Card className="border-[var(--error)]/30">
              <CardHeader className="bg-[var(--error)]/10">
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Probation (8 Call Outs)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {disciplineReport.probation_list.map(worker => (
                    <div key={worker.worker_id} className="flex items-center justify-between p-3 bg-[var(--error)]/10 rounded-lg">
                      <span className="font-medium">{worker.worker_name}</span>
                      <span className="text-sm text-[var(--error)]">{worker.approved_callouts} call outs</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 2nd Warning */}
          {disciplineReport?.second_warning_list?.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="text-orange-700 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  2nd Warning (5-7 Call Outs)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {disciplineReport.second_warning_list.map(worker => (
                    <div key={worker.worker_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <span className="font-medium">{worker.worker_name}</span>
                      <span className="text-sm text-orange-600">{worker.approved_callouts} call outs</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warning */}
          {disciplineReport?.warning_list?.length > 0 && (
            <Card className="border-[var(--warning)]/30">
              <CardHeader className="bg-[var(--warning)]/10">
                <CardTitle className="text-yellow-700 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Warning List (3-4 Call Outs)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {disciplineReport.warning_list.map(worker => (
                    <div key={worker.worker_id} className="flex items-center justify-between p-3 bg-[var(--warning)]/10 rounded-lg">
                      <span className="font-medium">{worker.worker_name}</span>
                      <span className="text-sm text-[var(--warning)]">{worker.approved_callouts} call outs</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No issues */}
          {!disciplineReport?.warning_list?.length && 
           !disciplineReport?.second_warning_list?.length && 
           !disciplineReport?.probation_list?.length && 
           !disciplineReport?.deactivated_list?.length && (
            <Card className="bg-[var(--success)]/10 border-[var(--success)]/30">
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-16 w-16 mx-auto text-green-400 mb-4" />
                <h3 className="text-lg font-medium text-[var(--success)]">All Workers in Good Standing</h3>
                <p className="text-[var(--success)]">No workers have reached the warning threshold.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* All Call Outs View */}
      {view === 'all' && (
        <Card>
          <CardHeader>
            <CardTitle>All Call Outs ({disciplineReport?.year})</CardTitle>
          </CardHeader>
          <CardContent>
            {allCallouts.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <p>No call outs recorded this year</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allCallouts.map(callout => (
                  <div 
                    key={callout.id}
                    className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{callout.worker_name}</span>
                        {getStatusBadge(callout.status)}
                        {callout.is_short_notice && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                            Short Notice
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[var(--text-secondary)]">
                        {callout.shift_date} - {callout.service_name} ({callout.callout_type})
                      </p>
                    </div>
                    <span className="text-sm text-[var(--text-muted)]">
                      {new Date(callout.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CallOutsManagementTab;
